import React, { useEffect, useState, useRef } from "react";
import { Clock, Star, ExternalLink, CheckCircle2, Hand, MoreHorizontal, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import LeadDrawer from "@/components/LeadDrawer";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useIsMobile } from "@/hooks/useIsMobile";
import { CATEGORY_COLORS, STAGE_COLORS, fmtDate } from "@/lib/leadUtils";

/* ============================================================================
 * Phase 7.0 — Follow-ups (Screen J) visual restyle
 * Warm-ivory editorial system · ~10 % looser density than Catalog R1 so the
 * lead metadata (name, chips, note, FU count) stays comfortably readable.
 * VISUAL-ONLY: every data-testid, native <button>/<input>, event handler and
 * state hook is preserved verbatim from the previous implementation.
 * ========================================================================== */

const SECTIONS = [
  { key: "overdue",   title: "Overdue",   color: "#A35C45" },   /* --ds-neg */
  { key: "due_today", title: "Due today", color: "#76705F" },   /* --ds-text-2 */
  { key: "upcoming",  title: "Upcoming",  color: "#22382C" },   /* --ds-ink */
];

export default function Today() {
  const [data, setData] = useState({ overdue: [], due_today: [], upcoming: [] });
  const [drawerId, setDrawerId] = useState(null);
  const [pickingDateFor, setPickingDateFor] = useState(null);

  const refresh = async () => {
    const { data } = await api.get("/followups/list");
    setData(data);
  };

  useEffect(() => { refresh(); }, []);

  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const done = async (leadId, responded) => {
    await api.post(`/leads/${leadId}/followup/done`, { lead_responded: responded });
    toast.success(responded === true ? "Marked done · responded" : responded === false ? "Marked done · no response" : "Marked done");
    refresh();
  };

  const snooze = async (leadId, days, date) => {
    const params = date ? { date } : { days };
    await api.post(`/leads/${leadId}/followup/snooze`, null, { params });
    setPickingDateFor(null);
    refresh();
  };

  // Module 6D — Dismiss a follow-up. Instant delete-from-list with a
  // 6-second Undo toast (sonner action). Undo POSTs to /undismiss with the
  // pre-dismiss snapshot the backend returns; that atomically restores the
  // next_followup_at + note and clears followup_dismissed_at.
  const dismiss = async (leadId) => {
    try {
      const { data } = await api.post(`/leads/${leadId}/followup/dismiss`);
      const snap = data?.snapshot || {};
      refresh();
      toast.success("Follow-up dismissed", {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/leads/${leadId}/followup/undismiss`, {
                next_followup_at:   snap.next_followup_at,
                next_followup_note: snap.next_followup_note,
              });
              toast.message("Follow-up restored");
              refresh();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Undo failed");
            }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't dismiss");
    }
  };

  return (
    <Layout>
      <div className="fade-up" data-testid="today-page">
        {/* Gold eyebrow with today's date */}
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "#B08D55",
            marginBottom: 8,
          }}
        >
          {today}
        </div>
        <h1
          className="font-display today-h1"
          style={{ fontSize: 52, lineHeight: 1.05, color: "#22382C", marginBottom: 28 }}
          data-testid="today-title"
        >
          Follow-ups
        </h1>
        <style>{`
          @media (max-width: 1023px) {
            .today-h1 { font-size: 34px !important; margin-bottom: 20px !important; }
          }
        `}</style>

        <OperationsSection />

        {SECTIONS.map((s) => (
          <section key={s.key} className="mb-10" data-testid={`section-${s.key}`}>
            <SectionHeader title={s.title} color={s.color} count={data[s.key].length} />
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {data[s.key].length === 0 ? (
                <div
                  style={{
                    padding: "26px 18px",
                    textAlign: "center",
                    fontSize: 13,
                    fontStyle: "italic",
                    color: "#76705F",
                    background: "#FBF8F1",
                    border: "1.5px dashed rgba(34,56,44,0.15)",
                    borderRadius: 14,
                  }}
                >
                  Nothing here.
                </div>
              ) : data[s.key].map((l) => (
                <FollowupCard
                  key={l.lead_id}
                  lead={l}
                  bucket={s.key}
                  accent={s.color}
                  onDone={done}
                  onSnooze={snooze}
                  onDismiss={dismiss}
                  onOpen={() => setDrawerId(l.lead_id)}
                  picking={pickingDateFor === l.lead_id}
                  onPick={() => setPickingDateFor(l.lead_id)}
                />
              ))}
            </div>
          </section>
        ))}

        <LeadDrawer leadId={drawerId} open={!!drawerId} onClose={() => setDrawerId(null)} onChange={refresh} />
      </div>

      {/* Shared button styles for ghost / primary actions used across the page */}
      <style>{`
        .fu-ghost-btn{
          padding:6px 11px;
          font-size:11px;
          font-weight:600;
          letter-spacing:0.04em;
          border:1px solid #E4DCCD;
          background:#FFFFFF;
          color:#2A2722;
          border-radius:6px;
          transition:border-color .15s, color .15s, background .15s;
          cursor:pointer;
        }
        .fu-ghost-btn:hover{border-color:#22382C;color:#22382C;background:#FAF6EC;}
        .fu-primary-btn{
          padding:6px 12px;
          font-size:11px;
          font-weight:600;
          letter-spacing:0.06em;
          text-transform:uppercase;
          background:#22382C;
          color:#F4EFE4;
          border:1px solid #22382C;
          border-radius:6px;
          cursor:pointer;
          transition:background .15s, border-color .15s;
        }
        .fu-primary-btn:hover{background:#2C4A3B;border-color:#2C4A3B;}
        .fu-gold-btn{
          padding:6px 12px;
          font-size:11px;
          font-weight:600;
          letter-spacing:0.06em;
          text-transform:uppercase;
          background:#B08D55;
          color:#FFFFFF;
          border:1px solid #B08D55;
          border-radius:6px;
          cursor:pointer;
          transition:background .15s, border-color .15s;
        }
        .fu-gold-btn:hover{background:#9A7A3C;border-color:#9A7A3C;}
        .fu-icon-btn{
          padding:6px;
          color:#76705F;
          background:transparent;
          border:1px solid transparent;
          border-radius:6px;
          cursor:pointer;
          transition:color .15s, border-color .15s, background .15s;
        }
        .fu-icon-btn:hover{color:#22382C;border-color:#E4DCCD;background:#FAF6EC;}
        .fu-date-input{
          padding:5px 8px;
          font-size:11px;
          font-weight:500;
          border:1px solid #E4DCCD;
          background:#FFFFFF;
          color:#2A2722;
          border-radius:6px;
          font-family:var(--ds-body-font);
        }
      `}</style>
    </Layout>
  );
}

function SectionHeader({ title, color, count }) {
  const isMobile = useIsMobile();
  if (isMobile) {
    // Mobile: colored eyebrow + pill count matching mockup
    const upperTitle = title.toUpperCase();
    const isNeg = color === "#A35C45";
    const isMuted = color === "#76705F";
    const pillBg = isNeg ? "rgba(162,75,59,0.12)" : isMuted ? "rgba(34,56,44,0.06)" : "rgba(176,141,85,0.15)";
    const pillFg = isNeg ? "#8E3A2C" : isMuted ? "rgba(34,56,44,0.4)" : "#95643C";
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 9, margin: "0 2px 12px" }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.14em",
            color: isMuted ? "rgba(34,56,44,0.45)" : color,
          }}
        >
          {upperTitle}
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: pillFg,
            background: pillBg,
            borderRadius: 999,
            padding: "1px 8px",
          }}
        >
          {String(count).padStart(2, "0")}
        </span>
      </div>
    );
  }
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 14,
        marginBottom: 14,
        paddingBottom: 10,
        borderBottom: `1px solid ${color}33`,
      }}
    >
      <div
        className="font-display"
        style={{
          fontSize: 22,
          lineHeight: 1.1,
          color,
          letterSpacing: "-0.005em",
        }}
      >
        {title}
      </div>
      <div
        className="ds-num"
        style={{ fontSize: 14, color: "#A39A8B" }}
      >
        {String(count).padStart(2, "0")}
      </div>
    </div>
  );
}

// Shared modal used by both mobile and desktop FollowupCard variants.
// Extracted to keep both branches DRY. Zero logic change — same handlers.
function FollowupResponseModal({ leadId, onDone, onClose }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        background: "rgba(34, 56, 44, 0.42)",
        backdropFilter: "blur(2px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#FFFFFF",
          padding: 24,
          maxWidth: 380,
          width: "100%",
          border: "1px solid #EDE5D8",
          borderRadius: 14,
          boxShadow: "0 18px 40px rgba(34,56,44,.18)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "#B08D55",
            marginBottom: 6,
          }}
        >
          Follow-up
        </div>
        <h2
          className="font-display"
          style={{ fontSize: 22, color: "#22382C", marginBottom: 8, lineHeight: 1.15 }}
        >
          Did the lead respond?
        </h2>
        <p style={{ fontSize: 12.5, color: "#76705F", marginBottom: 18, lineHeight: 1.5 }}>
          Tracking response rate helps spot leads going cold.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={async () => { await onDone(leadId, true); onClose(); }}
            className="fu-primary-btn"
            style={{ flex: 1, minWidth: 88, minHeight: 44, padding: "9px 12px", fontSize: 12 }}
            data-testid={`fu-modal-yes-${leadId}`}
          >
            Yes
          </button>
          <button
            onClick={async () => { await onDone(leadId, false); onClose(); }}
            style={{
              flex: 1,
              minWidth: 88,
              minHeight: 44,
              padding: "9px 12px",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              background: "#A35C45",
              color: "#FFFFFF",
              border: "1px solid #A35C45",
              borderRadius: 6,
              cursor: "pointer",
            }}
            data-testid={`fu-modal-no-${leadId}`}
          >
            No
          </button>
          <button
            onClick={() => { onDone(leadId, null); onClose(); }}
            className="fu-ghost-btn"
            style={{ flex: 1, minWidth: 88, minHeight: 44, padding: "9px 12px", fontSize: 12 }}
            data-testid={`fu-modal-skip-${leadId}`}
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * Module 6D — More-actions menu for a follow-up card.
 * Small "•••" trigger that reveals Snooze-1-week + Dismiss options. Kept
 * as a floating panel (not a modal) so the flow feels light. Click-outside
 * closes; Escape closes. Both mobile and desktop card branches use it.
 * ==========================================================================*/
function FollowupMoreMenu({ leadId, onSnooze, onDismiss }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={anchorRef} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="More actions"
        className="fu-icon-btn"
        data-testid={`fu-more-${leadId}`}
        style={{ minWidth: 44, minHeight: 44, borderRadius: 10 }}
      >
        <MoreHorizontal size={14} />
      </button>
      {open && (
        <div
          data-testid={`fu-more-menu-${leadId}`}
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 4px)",
            zIndex: 30,
            background: "#FFFFFF",
            border: "1px solid #E9E2D6",
            borderRadius: 10,
            boxShadow: "0 8px 20px rgba(34,56,44,.14)",
            padding: 5,
            minWidth: 180,
          }}
        >
          <button
            onClick={() => { setOpen(false); onSnooze(leadId, 7); }}
            data-testid={`fu-1w-${leadId}`}
            style={{
              display: "flex", alignItems: "center", gap: 8, width: "100%",
              padding: "9px 11px", background: "transparent", border: "none",
              borderRadius: 6, cursor: "pointer", fontSize: 13,
              color: "#22382C", textAlign: "left",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#F4EFE4"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <Clock size={13} strokeWidth={2.2} />
            Snooze 1 week
          </button>
          <button
            onClick={() => { setOpen(false); onDismiss(leadId); }}
            data-testid={`fu-dismiss-${leadId}`}
            style={{
              display: "flex", alignItems: "center", gap: 8, width: "100%",
              padding: "9px 11px", background: "transparent", border: "none",
              borderRadius: 6, cursor: "pointer", fontSize: 13,
              color: "#A35C45", textAlign: "left",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#FBEDE7"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <Trash2 size={13} strokeWidth={2.2} />
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}


function FollowupCard({ lead, bucket, accent, onDone, onSnooze, onDismiss, onOpen, picking, onPick }) {
  const [responseModal, setResponseModal] = useState(false);
  const isOverdue = bucket === "overdue";
  const isMobile = useIsMobile();

  // ============ MOBILE BRANCH ============
  // Simpler card matching the mockup: colored accent border-left, top row
  // with bucket-tone pill + due-date, Newsreader lead name, chip strip
  // (category / stage / FU count), italic note, action row that wraps
  // (Done / +1d / +3d / Date / open).
  if (isMobile) {
    const bucketLabel = bucket === "overdue" ? "OVERDUE" : bucket === "due_today" ? "DUE TODAY" : "UPCOMING";
    return (
      <div
        data-testid={`fu-card-${lead.lead_id}`}
        style={{
          background: "#FFFDFA",
          border: `1px solid ${isOverdue ? "rgba(162,75,59,0.2)" : "rgba(34,56,44,0.10)"}`,
          borderLeft: `4px solid ${accent}`,
          borderRadius: 14,
          padding: 15,
        }}
      >
        {/* Top pill row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 9 }}>
          <span
            style={{
              fontSize: 9.5,
              fontWeight: 800,
              letterSpacing: "0.07em",
              color: accent,
              background: isOverdue ? "rgba(162,75,59,0.12)" : "rgba(34,56,44,0.06)",
              padding: "4px 9px",
              borderRadius: 6,
            }}
          >
            {bucketLabel}
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              fontWeight: 600,
              color: "rgba(34,56,44,0.55)",
            }}
          >
            <Clock size={11} /> {fmtDate(lead.next_followup_at)}
          </span>
        </div>

        {/* Title */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
          <span
            data-testid={`fu-name-${lead.lead_id}`}
            style={{ fontFamily: "var(--ds-num-font)", fontSize: 17, fontWeight: 500, color: "#22382C" }}
          >
            {lead.full_name || "—"}
          </span>
          {lead.starred && <Star size={12} fill="#B08D55" color="#B08D55" />}
        </div>

        {/* Chips */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: lead.next_followup_note ? 8 : 12 }}>
          {lead.category && (
            <Chip color={CATEGORY_COLORS[lead.category]}>{lead.category}</Chip>
          )}
          <Chip color={{ bg: STAGE_COLORS[lead.stage], fg: "#fff" }}>{lead.stage}</Chip>
          {lead.followup_count > 0 && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                color: "#A39A8B",
              }}
            >
              FU {lead.followup_count}
            </span>
          )}
        </div>

        {/* Note */}
        {lead.next_followup_note && (
          <div
            style={{
              fontStyle: "italic",
              fontSize: 12.5,
              color: "rgba(34,56,44,0.65)",
              lineHeight: 1.45,
              marginBottom: 12,
            }}
          >
            &ldquo;{lead.next_followup_note}&rdquo;
          </div>
        )}

        {/* Actions — full-width Done + snooze grid + open */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", paddingTop: 10, borderTop: "1px solid rgba(34,56,44,0.08)" }}>
          <button
            onClick={() => setResponseModal(true)}
            data-testid={`fu-done-${lead.lead_id}`}
            style={{
              flex: "1 1 100%",
              minHeight: 44,
              padding: "10px 12px",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.04em",
              background: "#22382C",
              color: "#F7F3EC",
              border: "1px solid #22382C",
              borderRadius: 11,
              cursor: "pointer",
            }}
          >
            Done
          </button>
          <button
            onClick={() => onSnooze(lead.lead_id, 1)}
            className="fu-ghost-btn"
            data-testid={`fu-1d-${lead.lead_id}`}
            style={{ flex: 1, minHeight: 44, borderRadius: 10 }}
          >
            +1d
          </button>
          <button
            onClick={() => onSnooze(lead.lead_id, 3)}
            className="fu-ghost-btn"
            style={{ flex: 1, minHeight: 44, borderRadius: 10 }}
          >
            +3d
          </button>
          {picking ? (
            <input
              type="date"
              autoFocus
              onChange={(e) => onSnooze(lead.lead_id, null, e.target.value)}
              className="fu-date-input"
              data-testid={`fu-datepicker-${lead.lead_id}`}
              style={{ flex: 1, minHeight: 44 }}
            />
          ) : (
            <button
              onClick={onPick}
              className="fu-ghost-btn"
              data-testid={`fu-date-${lead.lead_id}`}
              style={{ flex: 1, minHeight: 44, borderRadius: 10 }}
            >
              Date
            </button>
          )}
          <button
            onClick={onOpen}
            title="Open"
            className="fu-icon-btn"
            data-testid={`fu-open-${lead.lead_id}`}
            style={{ minWidth: 44, minHeight: 44 }}
          >
            <ExternalLink size={14} />
          </button>
          {/* Module 6D — Snooze 1 week + Dismiss (mobile branch) */}
          <FollowupMoreMenu
            leadId={lead.lead_id}
            onSnooze={onSnooze}
            onDismiss={onDismiss}
          />
        </div>

        {responseModal && (
          <FollowupResponseModal
            leadId={lead.lead_id}
            onDone={onDone}
            onClose={() => setResponseModal(false)}
          />
        )}
      </div>
    );
  }

  // ============ DESKTOP BRANCH (original) ============
  return (
    <div
      data-testid={`fu-card-${lead.lead_id}`}
      className="fu-card-mobile-wrap"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        background: "#FFFFFF",
        border: "1px solid #EDE5D8",
        borderLeft: `3px solid ${accent}`,
        borderRadius: 10,
        padding: "15px 18px",
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
        transition: "box-shadow .15s ease, transform .15s ease",
        position: "relative",
      }}
    >
      <style>{`
        @media (max-width: 1023px) {
          .fu-card-mobile-wrap { flex-wrap: wrap !important; align-items: flex-start !important; gap: 10px !important; padding: 13px 14px !important; }
          .fu-card-mobile-wrap > div:last-child { width: 100%; gap: 8px !important; flex-wrap: wrap; }
          .fu-card-mobile-wrap > div:last-child button { min-height: 44px; }
          .fu-card-mobile-wrap > div:last-child .fu-primary-btn { flex: 1; }
        }
      `}</style>
      {isOverdue && (
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: accent,
            flexShrink: 0,
            boxShadow: `0 0 0 3px ${accent}22`,
          }}
        />
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span
            data-testid={`fu-name-${lead.lead_id}`}
            style={{ fontSize: 14, fontWeight: 600, color: "#22382C" }}
          >
            {lead.full_name || "—"}
          </span>
          {lead.starred && <Star size={12} fill="#B08D55" color="#B08D55" />}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
          {lead.category && (
            <Chip color={CATEGORY_COLORS[lead.category]}>{lead.category}</Chip>
          )}
          <Chip color={{ bg: STAGE_COLORS[lead.stage], fg: "#fff" }}>{lead.stage}</Chip>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              color: "#76705F",
            }}
          >
            <Clock size={11} /> {fmtDate(lead.next_followup_at)}
          </span>
          {lead.followup_count > 0 && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                color: "#A39A8B",
              }}
            >
              FU {lead.followup_count}
            </span>
          )}
        </div>
        {lead.next_followup_note && (
          <div
            style={{
              fontStyle: "italic",
              fontSize: 12,
              color: "#76705F",
              marginTop: 6,
              lineHeight: 1.4,
            }}
          >
            &ldquo;{lead.next_followup_note}&rdquo;
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        <button
          onClick={() => setResponseModal(true)}
          className="fu-primary-btn"
          data-testid={`fu-done-${lead.lead_id}`}
        >
          Done
        </button>
        <button
          onClick={() => onSnooze(lead.lead_id, 1)}
          className="fu-ghost-btn"
          data-testid={`fu-1d-${lead.lead_id}`}
        >
          +1d
        </button>
        <button
          onClick={() => onSnooze(lead.lead_id, 3)}
          className="fu-ghost-btn"
        >
          +3d
        </button>
        {picking ? (
          <input
            type="date"
            autoFocus
            onChange={(e) => onSnooze(lead.lead_id, null, e.target.value)}
            className="fu-date-input"
            data-testid={`fu-datepicker-${lead.lead_id}`}
          />
        ) : (
          <button
            onClick={onPick}
            className="fu-ghost-btn"
            data-testid={`fu-date-${lead.lead_id}`}
          >
            Date
          </button>
        )}
        <button
          onClick={onOpen}
          title="Open"
          className="fu-icon-btn"
          data-testid={`fu-open-${lead.lead_id}`}
        >
          <ExternalLink size={14} />
        </button>
        {/* Module 6D — Snooze 1 week + Dismiss (desktop branch) */}
        <FollowupMoreMenu
          leadId={lead.lead_id}
          onSnooze={onSnooze}
          onDismiss={onDismiss}
        />
      </div>

      {responseModal && (
        <FollowupResponseModal
          leadId={lead.lead_id}
          onDone={onDone}
          onClose={() => setResponseModal(false)}
        />
      )}
    </div>
  );
}


// ============================================================================
// Module 6B — Operations section (live operational tasks queue)
// ============================================================================
const SEVERITY_STYLE = {
  Overdue:  { color: "#A35C45", label: "OVERDUE",  bg: "#F7E2D9" },
  Critical: { color: "#B45309", label: "CRITICAL", bg: "#FBE6CC" },
  Warning:  { color: "#A16207", label: "WARNING",  bg: "#F6EBD0" },
  Notice:   { color: "#76705F", label: "NOTICE",   bg: "#F1EFE6" },
};

function OperationsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const { data } = await api.get("/tasks");
      setItems(data.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onClaim = async (t) => {
    const targetId = t.project_id || t.lead_id;
    try {
      await api.post("/tasks/claim", { rule_id: t.rule_id, target_id: targetId });
      toast.success("Claimed");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed");
    }
  };

  const onRelease = async (t) => {
    const targetId = t.project_id || t.lead_id;
    try {
      await api.delete("/tasks/claim", { data: { rule_id: t.rule_id, target_id: targetId } });
      toast.success("Released");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Release failed");
    }
  };

  // Module 6L — Ops-task Dismiss / Snooze / Undismiss.
  // Optimistic-hide + 6s Undo toast matching /today follow-ups. Undismiss
  // via /tasks/undismiss restores the exact (rule_id, target_id) pair.
  // balance_overdue permanent-dismiss is 422 by design (money owed) — the
  // OpsMoreMenu hides the Dismiss item for that rule, but we still surface
  // the backend error message via toast if a stale menu state ever slips
  // through.
  const undismissTask = async (rule_id, target_id) => {
    try {
      await api.post("/tasks/undismiss", { rule_id, target_id });
      toast.message("Task restored");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  const onDismissTask = async (t) => {
    const target_id = t.project_id || t.lead_id;
    try {
      await api.post("/tasks/dismiss", { rule_id: t.rule_id, target_id });
      refresh();
      toast.success("Task dismissed", {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: () => undismissTask(t.rule_id, target_id),
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't dismiss");
    }
  };

  const onSnoozeTask = async (t, opts) => {
    // opts: { days } OR { until } — passed verbatim to backend.
    const target_id = t.project_id || t.lead_id;
    try {
      await api.post("/tasks/snooze", { rule_id: t.rule_id, target_id, ...opts });
      refresh();
      const label = opts.days
        ? `Snoozed for ${opts.days} day${opts.days === 1 ? "" : "s"}`
        : `Snoozed until ${opts.until}`;
      toast.success(label, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: () => undismissTask(t.rule_id, target_id),
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't snooze");
    }
  };

  if (loading) return null;

  return (
    <section className="mb-12" data-testid="ops-section">
      <SectionHeader title="Operations" color="#22382C" count={items.length} />
      {items.length === 0 ? (
        <div
          data-testid="ops-empty"
          style={{
            padding: "26px 18px",
            textAlign: "center",
            fontSize: 13,
            fontStyle: "italic",
            color: "#76705F",
            background: "#FAF6EC",
            border: "1px dashed #E4DCCD",
            borderRadius: 10,
          }}
        >
          No operational tasks right now — you&apos;re all caught up.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((t) => (
            <OpsCard
              key={`${t.rule_id}-${t.project_id || t.lead_id}`}
              task={t}
              currentUserId={user?.user_id}
              onClaim={() => onClaim(t)}
              onRelease={() => onRelease(t)}
              onGo={() => navigate(t.action_route)}
              onDismiss={() => onDismissTask(t)}
              onSnooze={(opts) => onSnoozeTask(t, opts)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function OpsMoreMenu({ tid, task, onSnooze, onDismiss }) {
  // Module 6L — parallel to FollowupMoreMenu for /today lead follow-ups.
  // Snooze options: 3d, 1w, and (event-aware) "until day before event".
  // Dismiss item is hidden for `balance_overdue` — server also enforces 422
  // for that rule (defense-in-depth).
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // "Until day before event" only when event_date exists AND we have at
  // least 2 whole days between today and the event (else the snooze would
  // expire before it takes effect).
  const eventDate = task.event_date;
  const days = task.days_to_event;
  const showUntilEvent = Boolean(eventDate) && typeof days === "number" && days >= 2;

  const snoozeUntilEvent = () => {
    // Snooze until end-of-day of (event_date - 1 day) in UTC.
    // event_date is YYYY-MM-DD; subtract one day with basic arithmetic.
    const d = new Date(`${eventDate}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 1);
    const iso = d.toISOString().slice(0, 10);
    onSnooze({ until: iso });
  };

  const isBalanceOverdue = task.rule_id === "balance_overdue";

  const menuItem = (extra) => ({
    display: "flex", alignItems: "center", gap: 8, width: "100%",
    padding: "9px 11px", background: "transparent", border: "none",
    borderRadius: 6, cursor: "pointer", fontSize: 13,
    textAlign: "left", color: "#22382C", ...extra,
  });

  return (
    <div ref={anchorRef} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="More actions"
        className="fu-icon-btn"
        data-testid={`ops-more-${tid}`}
        style={{ minWidth: 34, minHeight: 34, borderRadius: 8,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  background: "transparent", border: "1px solid transparent",
                  cursor: "pointer", color: "#76705F" }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#E4DCCD"; e.currentTarget.style.color = "#22382C"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "transparent"; e.currentTarget.style.color = "#76705F"; }}
      >
        <MoreHorizontal size={14} />
      </button>
      {open && (
        <div
          data-testid={`ops-more-menu-${tid}`}
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 4px)",
            zIndex: 30,
            background: "#FFFFFF",
            border: "1px solid #E9E2D6",
            borderRadius: 10,
            boxShadow: "0 8px 20px rgba(34,56,44,.14)",
            padding: 5,
            minWidth: 210,
          }}
        >
          <button
            onClick={() => { setOpen(false); onSnooze({ days: 3 }); }}
            data-testid={`ops-snooze-3d-${tid}`}
            style={menuItem()}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#F4EFE4"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <Clock size={13} strokeWidth={2.2} />
            Snooze 3 days
          </button>
          <button
            onClick={() => { setOpen(false); onSnooze({ days: 7 }); }}
            data-testid={`ops-snooze-1w-${tid}`}
            style={menuItem()}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#F4EFE4"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <Clock size={13} strokeWidth={2.2} />
            Snooze 1 week
          </button>
          {showUntilEvent && (
            <button
              onClick={() => { setOpen(false); snoozeUntilEvent(); }}
              data-testid={`ops-snooze-until-event-${tid}`}
              style={menuItem()}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#F4EFE4"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <Clock size={13} strokeWidth={2.2} />
              Snooze until day before event
            </button>
          )}
          {!isBalanceOverdue && (
            <button
              onClick={() => { setOpen(false); onDismiss(); }}
              data-testid={`ops-dismiss-${tid}`}
              style={menuItem({ color: "#A35C45" })}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#FBEDE7"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <Trash2 size={13} strokeWidth={2.2} />
              Dismiss
            </button>
          )}
        </div>
      )}
    </div>
  );
}


function OpsCard({ task, currentUserId, onClaim, onRelease, onGo, onDismiss, onSnooze }) {
  const sev = SEVERITY_STYLE[task.severity] || SEVERITY_STYLE.Notice;
  const claim = task.claimed_by;
  const isMyClaim = claim && claim.user_id === currentUserId;
  const isPeerClaim = claim && claim.user_id !== currentUserId;
  const tid = `${task.rule_id}-${task.project_id || task.lead_id}`;
  const isMobile = useIsMobile();

  // ============ MOBILE BRANCH ============
  // Matches the mockup: 4px gold accent border-left, top pill row
  // [SEVERITY] [event_date · role], Newsreader client title, task detail,
  // "CLAIMED BY YOU / peer" pill with mini avatar, action row
  // [Release/Claim] [primary Action] stacked as full-width buttons.
  if (isMobile) {
    return (
      <div
        data-testid={`ops-card-${tid}`}
        style={{
          background: "#FFFDFA",
          border: "1px solid rgba(34,56,44,0.10)",
          borderLeft: `4px solid ${sev.color}`,
          borderRadius: 14,
          padding: 15,
        }}
      >
        {/* Top pill row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 9 }}>
          <span
            data-testid={`ops-severity-${tid}`}
            style={{
              fontSize: 9.5,
              fontWeight: 800,
              letterSpacing: "0.07em",
              color: sev.color,
              background: sev.bg,
              padding: "4px 9px",
              borderRadius: 6,
            }}
          >
            {sev.label}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "rgba(34,56,44,0.45)",
              }}
            >
              {task.event_date && (
                <>
                  {task.days_to_event !== null && task.days_to_event !== undefined
                    ? (task.days_to_event < 0 ? `${-task.days_to_event}d ago` : `${task.days_to_event}d to go`)
                    : fmtDate(task.event_date)}
                </>
              )}
              {task.event_date && task.assignee_role && " · "}
              {task.assignee_role}
            </span>
            <OpsMoreMenu tid={tid} task={task} onSnooze={onSnooze} onDismiss={onDismiss} />
          </div>
        </div>

        {/* Title (client name) */}
        <div
          data-testid={`ops-client-${tid}`}
          style={{
            fontFamily: "var(--ds-num-font)",
            fontSize: 18,
            fontWeight: 500,
            color: "#22382C",
            marginBottom: 3,
          }}
        >
          {task.client_name}
        </div>

        {/* Task detail */}
        <div
          style={{
            fontSize: 13,
            color: "rgba(34,56,44,0.6)",
            lineHeight: 1.45,
            marginBottom: 11,
          }}
        >
          {task.detail}
        </div>

        {/* Claimed-by pill */}
        {claim && (
          <div
            data-testid={`ops-claimed-by-${tid}`}
            title={`Claimed at ${claim.claimed_at}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "#EFE8DA",
              padding: "5px 10px",
              borderRadius: 999,
              marginBottom: 13,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: "linear-gradient(135deg,#C9A46B,#A07C46)",
                display: "grid",
                placeItems: "center",
                fontFamily: "var(--ds-num-font)",
                fontSize: 8,
                fontWeight: 600,
                color: "#F7F3EC",
              }}
            >
              {(claim.name || "?").slice(0, 2).toUpperCase()}
            </span>
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "rgba(34,56,44,0.6)",
              }}
            >
              {isMyClaim ? "Claimed by you" : `Claimed by ${claim.name}`}
            </span>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 10 }}>
          {!isPeerClaim && !isMyClaim && (
            <button
              onClick={onClaim}
              data-testid={`ops-claim-${tid}`}
              style={{
                flex: 1,
                minHeight: 44,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                background: "#EFE8DA",
                color: "#22382C",
                border: "1px solid #EFE8DA",
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <Hand size={13} /> Claim
            </button>
          )}
          {isMyClaim && (
            <button
              onClick={onRelease}
              data-testid={`ops-release-${tid}`}
              style={{
                flex: 1,
                minHeight: 44,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                background: "#EFE8DA",
                color: "#22382C",
                border: "1px solid #EFE8DA",
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <CheckCircle2 size={13} /> Release
            </button>
          )}
          <button
            onClick={onGo}
            data-testid={`ops-action-${tid}`}
            style={{
              flex: 2,
              minHeight: 44,
              background: "#22382C",
              color: "#F7F3EC",
              border: "1px solid #22382C",
              borderRadius: 12,
              fontSize: 12.5,
              fontWeight: 700,
              letterSpacing: "0.02em",
              cursor: "pointer",
            }}
          >
            {task.action_label}
          </button>
        </div>
      </div>
    );
  }

  // ============ DESKTOP BRANCH (original) ============
  return (
    <div
      data-testid={`ops-card-${tid}`}
      className="ops-card-mobile-wrap"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        background: "#FFFFFF",
        border: "1px solid #EDE5D8",
        borderLeft: `3px solid ${sev.color}`,
        borderRadius: 10,
        padding: "15px 18px",
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
        transition: "box-shadow .15s ease, transform .15s ease",
      }}
    >
      {/* Mobile CSS: wrap to a column so the middle info + action buttons
          each get full width and the severity chip sits on its own row. */}
      <style>{`
        @media (max-width: 1023px) {
          .ops-card-mobile-wrap { flex-wrap: wrap !important; align-items: flex-start !important; gap: 10px !important; padding: 13px 14px !important; }
          .ops-card-mobile-wrap > div:last-child { width: 100%; }
          .ops-card-mobile-wrap > div:last-child button { flex: 1; min-height: 44px; }
        }
      `}</style>
      <span
        data-testid={`ops-severity-${tid}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "3px 8px",
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          background: sev.bg,
          color: sev.color,
          borderRadius: 4,
          flexShrink: 0,
        }}
      >
        {sev.label}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span
            data-testid={`ops-client-${tid}`}
            style={{ fontSize: 14, fontWeight: 600, color: "#22382C" }}
          >
            {task.client_name}
          </span>
          <span style={{ fontSize: 11, color: "#A39A8B" }}>·</span>
          <span style={{ fontSize: 12, color: "#76705F" }}>{task.detail}</span>
          {claim && (
            <span
              data-testid={`ops-claimed-by-${tid}`}
              title={`Claimed at ${claim.claimed_at}`}
              style={{
                fontSize: 9.5,
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                padding: "2px 7px",
                background: "#F1EFE6",
                color: "#76705F",
                borderRadius: 4,
              }}
            >
              Claimed by {claim.name}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6, flexWrap: "wrap" }}>
          {task.event_date && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "#76705F" }}>
              <Clock size={11} /> {fmtDate(task.event_date)}
              {task.days_to_event !== null && task.days_to_event !== undefined && (
                <span> · {task.days_to_event < 0 ? `${-task.days_to_event}d ago` : `${task.days_to_event}d to go`}</span>
              )}
            </span>
          )}
          <span
            style={{
              fontSize: 9.5,
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#A39A8B",
            }}
          >
            {task.assignee_role}
          </span>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        {!isPeerClaim && !isMyClaim && (
          <button
            onClick={onClaim}
            className="fu-ghost-btn"
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            data-testid={`ops-claim-${tid}`}
          >
            <Hand size={11} /> Claim
          </button>
        )}
        {isMyClaim && (
          <button
            onClick={onRelease}
            className="fu-ghost-btn"
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            data-testid={`ops-release-${tid}`}
          >
            <CheckCircle2 size={11} /> Release
          </button>
        )}
        <button
          onClick={onGo}
          className="fu-primary-btn"
          data-testid={`ops-action-${tid}`}
        >
          {task.action_label}
        </button>
        <OpsMoreMenu tid={tid} task={task} onSnooze={onSnooze} onDismiss={onDismiss} />
      </div>
    </div>
  );
}

function Chip({ color, children }) {
  return (
    <span
      style={{
        padding: "2px 7px",
        fontSize: 9.5,
        fontWeight: 600,
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        background: color?.bg || "#F1EFE6",
        color: color?.fg || "#2A2722",
        borderRadius: 999,
        border: color?.border ? `1px solid ${color.border}` : undefined,
      }}
    >
      {children}
    </span>
  );
}
